// src/pages/AdminGrades.jsx
import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft,
  Edit3,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle
} from '../components/icons';
import Swal from 'sweetalert2';
import './AdminGrades.css';
import {groupMoney,moneyValue} from '../utils/moneyInput';
const editDialog=Swal.mixin({
  width:560,padding:0,buttonsStyling:false,reverseButtons:true,showCloseButton:true,
  closeButtonAriaLabel:'بستن پنجره ویرایش',backdrop:'#132d43a3',
  customClass:{popup:'esf-admin-dialog',title:'esf-admin-dialog-title',htmlContainer:'esf-admin-dialog-description',input:'esf-admin-dialog-input',inputLabel:'esf-admin-dialog-label',actions:'esf-admin-dialog-actions',confirmButton:'esf-admin-dialog-confirm',cancelButton:'esf-admin-dialog-cancel',closeButton:'esf-admin-dialog-close',validationMessage:'esf-admin-dialog-validation'}
});
const priceInput=value=>({input:'text',inputValue:groupMoney(value),inputAttributes:{inputmode:'decimal',dir:'ltr'},preConfirm:moneyValue,
 inputValidator:value=>!moneyValue(value)||!/^\d+(?:\.\d+)?$/.test(moneyValue(value))||!Number.isFinite(Number(moneyValue(value)))?'مبلغ معتبر و غیرمنفی وارد کنید.':undefined,
 didOpen:()=>{const input=editDialog.getInput();input.addEventListener('input',()=>{const caret=input.selectionStart??input.value.length,leftDigits=moneyValue(input.value.slice(0,caret)).length;input.value=groupMoney(input.value);let position=0,count=0;while(position<input.value.length&&count<leftDigits){if(input.value[position]!==',')count++;position++;}input.setSelectionRange(position,position);});}
});

// نمایش پیام
const showAlert = (message, type) => {
  Swal.fire({
    text: message,
    icon: type,
    toast: true,
    position: 'top',
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false,
    customClass: { popup: 'font-vazir' },
  });
};

// فرمت اعداد
const formatNumber = (value) => {
  if (!value) return '0';
  return parseFloat(value).toLocaleString('fa-IR');
};

export default function AdminGrades() {
  const [grades, setGrades] = useState([]);
  const [users, setUsers] = useState([]);
  const [prices, setPrices] = useState([]);
  const [basePrice, setBasePrice] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      if (err.response?.status !== 401 && localStorage.getItem('refreshToken')) throw err;
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  const fetchData = async (useToken = token) => {
    try {
      const [gradesRes, usersRes, pricesRes] = await Promise.all([
        api.get('/api/admin/grades', { headers: { Authorization: `Bearer ${useToken}` } }),
        api.get('/api/admin/users', { headers: { Authorization: `Bearer ${useToken}` } }),
        api.get('/api/admin/prices', { headers: { Authorization: `Bearer ${useToken}` } }),
      ]);

      setGrades(gradesRes.data);
      setUsers(usersRes.data);
      setPrices(pricesRes.data.prices);
      setBasePrice(pricesRes.data.base_price);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          await fetchData(newToken);
        } else {
          showAlert('جلسه شما منقضی شده است.', 'error');
          setTimeout(() => navigate('/login_page'), 2000);
        }
      } else {
        setError(err.response?.data?.message || 'خطا در دریافت اطلاعات');
      }
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }
    fetchData();
  }, [token, navigate]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // ویرایش قوانین گرید
  const handleEditGrade = async (id) => {
    const grade = grades.find(g => g.id === id);
    const { value: sell_add } = await editDialog.fire({
      titleText: `ویرایش قیمت گرید ${id}`,
      text:'مرحله ۱ از ۲ · مبلغ را به ریال وارد کنید.',
      ...priceInput(grade.sell_add),
      inputLabel: 'از قیمت کسر شود (ریال)',
      showCancelButton: true,
      confirmButtonText: 'بعدی',
      cancelButtonText: 'انصراف',
    });

    if (sell_add === undefined) return;

    const { value: buy_subtract } = await editDialog.fire({
      titleText: `ویرایش قیمت گرید ${id}`,
      text:'مرحله ۲ از ۲ · پس از تأیید، تغییرات ذخیره می‌شود.',
      ...priceInput(grade.buy_subtract),
      inputLabel: 'به قیمت اضافه شود (ریال)',
      showCancelButton: true,
      confirmButtonText: 'ثبت تغییرات',
      cancelButtonText: 'انصراف',
    });

    if (buy_subtract === undefined) return;

    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');

      await api.put(`/api/admin/grades/${id}`, {
        sell_add: parseFloat(sell_add),
        buy_subtract: parseFloat(buy_subtract)
      }, {
        headers: { Authorization: `Bearer ${newToken}` }
      });

      setGrades(grades.map(g => g.id === id ? {
        ...g,
        sell_add: parseFloat(sell_add),
        buy_subtract: parseFloat(buy_subtract)
      } : g));

      const pricesRes = await api.get('/api/admin/prices', {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setPrices(pricesRes.data.prices);
      setBasePrice(pricesRes.data.base_price);
      setSuccess('قوانین گرید ویرایش شد');
    } catch (err) {
      showAlert(err.response?.data?.message || 'خطا در ویرایش', 'error');
    }
  };

  // ویرایش گرید کاربر
  const handleEditUserGrade = async (id) => {
    const user = users.find(u => u.id === id);
    const { value: grade } = await editDialog.fire({
      titleText: 'تغییر گرید کاربر',
      text:`${user.first_name} ${user.last_name || ''} · گرید جدید را انتخاب کنید.`,
      input: 'select',
      inputOptions: { 1: '1', 2: '2', 3: '3', 4: '4', 5: '5' },
      inputValue: user.grade,
      showCancelButton: true,
      confirmButtonText: 'ثبت',
      cancelButtonText: 'انصراف',
    });

    if (!grade) return;

    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');

      await api.put(`/api/admin/users/${id}`, { grade: parseInt(grade) }, {
        headers: { Authorization: `Bearer ${newToken}` }
      });

      setUsers(users.map(u => u.id === id ? { ...u, grade: parseInt(grade) } : u));
      setSuccess('گرید کاربر ویرایش شد');
    } catch (err) {
      showAlert(err.response?.data?.message || 'خطا در ویرایش', 'error');
    }
  };

  // ویرایش کد سیستم کاربر
  const handleEditSystemCode = async (id) => {
    const user = users.find(u => u.id === id);
    const currentCode = user.system_code || '';

    const { value: system_code } = await editDialog.fire({
      titleText:'تغییر کد سیستم',
      text:`${user.first_name} ${user.last_name || ''} · کد سیستم باید برای این کاربر یکتا باشد.`,
      input: 'text',
      inputLabel: 'کد سیستم (یکتا)',
      inputPlaceholder: 'مثلاً: GOLD123',
      inputValue: currentCode,
      showCancelButton: true,
      confirmButtonText: currentCode ? 'ویرایش' : 'ثبت',
      cancelButtonText: 'انصراف',
      inputValidator: (value) => {
        if (!value || value.trim() === '') {
          return 'کد سیستم نمی‌تواند خالی باشد';
        }
        if (value.trim().length > 50) {
          return 'کد سیستم نباید بیشتر از 50 کاراکتر باشد';
        }
      },
      preConfirm: (value) => value.trim()
    });

    if (!system_code) return;

    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');

      await api.put(`/api/admin/users/${id}/system-code`, { system_code }, {
        headers: { Authorization: `Bearer ${newToken}` }
      });

      setUsers(users.map(u => u.id === id ? { ...u, system_code } : u));
      setSuccess('کد سیستم با موفقیت ثبت شد');
    } catch (err) {
      const msg = err.response?.data?.message || 'خطا در ثبت کد سیستم';
      showAlert(msg, 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 px-4 py-8 font-vazir">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* برگشت */}
        <motion.button
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-teal-700 mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          برگشت
        </motion.button>

        {/* هدر */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-3xl flex items-center justify-center text-white shadow-xl">
              <DollarSign className="w-9 h-9" />
            </div>
          </div>
          <h2 data-page-title="true" className="text-3xl font-bold text-gray-800">مدیریت گریدها و کاربران</h2>
          <p className="text-gray-600 mt-2">تنظیم قیمت‌ها، قوانین گرید و تخصیص به کاربران</p>
        </motion.div>

        {/* پیام‌ها */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3">
              <AlertCircle className="w-6 h-6" />
              {error}
            </motion.div>
          )}
          {success && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3">
              <CheckCircle className="w-6 h-6" />
              {success}
            </motion.div>
          )}
        </AnimatePresence>

        {/* قیمت پایه */}
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-3xl shadow-2xl p-6 text-center">
          <p className="text-lg font-medium">قیمت پایه طلا</p>
          <p className="text-3xl font-bold mt-2">{formatNumber(basePrice)} ریال</p>
        </motion.div>

        {/* جدول قیمت‌ها */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 border border-white/30">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-6 h-6 text-green-600" />
            <h3 className="text-xl font-bold text-gray-800">جدول قیمت‌های خرید و فروش</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-teal-50 to-cyan-50 text-gray-700">
                  <th className="p-4 text-right font-bold">گرید</th>
                  <th className="p-4 text-right font-bold">قیمت خرید</th>
                  <th className="p-4 text-right font-bold">قیمت فروش</th>
                </tr>
              </thead>
              <tbody>
                {prices.map((price) => (
                  <motion.tr key={price.grade} whileHover={{ backgroundColor: '#f0fdfa' }} className="border-b">
                    <td className="p-4 text-center font-mono text-teal-600">گرید {price.grade}</td>
                    <td className="p-4 font-mono text-green-700">{formatNumber(price.buy_price)}</td>
                    <td className="p-4 font-mono text-red-700">{formatNumber(price.sell_price)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* قوانین گریدها */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 border border-white/30">
          <div className="flex items-center gap-3 mb-6">
            <Edit3 className="w-6 h-6 text-indigo-600" />
            <h3 className="text-xl font-bold text-gray-800">قوانین قیمت گریدها</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 text-gray-700">
                  <th className="p-4 text-right font-bold">گرید</th>
                  <th className="p-4 text-right font-bold">از قیمت کسر شود</th>
                  <th className="p-4 text-right font-bold">به قیمت اضافه شود</th>
                  <th className="p-4 text-right font-bold">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {grades.map((grade) => (
                  <motion.tr key={grade.id} whileHover={{ backgroundColor: '#f8f5ff' }} className="border-b">
                    <td className="p-4 text-center font-mono text-indigo-600">گرید {grade.id}</td>
                    <td className="p-4 font-mono text-red-600">{formatNumber(grade.sell_add)}</td>
                    <td className="p-4 font-mono text-green-600">{formatNumber(grade.buy_subtract)}</td>
                    <td className="p-4 text-center">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleEditGrade(grade.id)}
                        className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-2 px-4 rounded-xl font-medium text-sm shadow-lg hover:shadow-xl transition-all"
                      >
                        ویرایش
                      </motion.button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* لیست کاربران */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 border border-white/30">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-6 h-6 text-blue-600" />
            <h3 className="text-xl font-bold text-gray-800">لیست کاربران</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-blue-50 to-cyan-50 text-gray-700">
                  <th className="p-4 text-right font-bold">نام</th>
                  <th className="p-4 text-right font-bold">شماره</th>
                  <th className="p-4 text-right font-bold">گرید</th>
                  <th className="p-4 text-right font-bold">کد سیستم</th>
                  <th className="p-4 text-right font-bold">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <motion.tr key={user.id} whileHover={{ backgroundColor: '#f0f9ff' }} className="border-b">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {user.first_name[0]}
                        </div>
                        <span className="font-medium">{user.first_name} {user.last_name}</span>
                      </div>
                    </td>
                    <td className="p-4 font-mono text-gray-700" dir="ltr">{user.phone_number}</td>
                    <td className="p-4 text-center">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                        گرید {user.grade}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      {user.system_code ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                          {user.system_code}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="p-4 text-center space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleEditUserGrade(user.id)}
                        className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white py-2 px-3 rounded-xl font-medium text-xs shadow-lg hover:shadow-xl transition-all"
                      >
                        گرید
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleEditSystemCode(user.id)}
                        className="bg-gradient-to-r from-purple-500 to-pink-600 text-white py-2 px-3 rounded-xl font-medium text-xs shadow-lg hover:shadow-xl transition-all"
                      >
                        کد سیستم
                      </motion.button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      <style jsx>{`.font-vazir { font-family: 'Vazir', sans-serif !important; }`}</style>
    </div>
  );
}
